(() => {
  const controllerKey = '__moztechPremiumHeaderController';

  class MoztechStickyHeader extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.shopify-section-header') || this;
      this.stickyType = this.dataset.stickyType || 'on-scroll-up';
      this.lastScrollTop = window.scrollY || 0;
      this.scrollDeltaThreshold = 6;
      this.frame = 0;
      this.onScroll = this.onScroll.bind(this);
      this.root.classList.toggle('moz-header--not-sticky', this.stickyType === 'none');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.sync(this.lastScrollTop);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.sync(window.scrollY || document.documentElement.scrollTop || 0);
      });
    }

    sync(scrollTop) {
      if (!this.root || this.stickyType === 'none') return;

      const compact = scrollTop > 18;
      const scrollDelta = scrollTop - this.lastScrollTop;
      const megaMenuInUse = this.root.querySelector(
        '.list-menu--megamenu-visible:hover, .list-menu--megamenu-visible:focus-within'
      );
      let shouldHide = this.root.classList.contains('shopify-section-header-hidden');

      if (this.stickyType !== 'on-scroll-up' || scrollTop <= 18 || megaMenuInUse) {
        shouldHide = false;
      } else if (scrollDelta > this.scrollDeltaThreshold && scrollTop > 120) {
        shouldHide = true;
      } else if (scrollDelta < -this.scrollDeltaThreshold) {
        shouldHide = false;
      }

      this.root.classList.toggle('shopify-section-header-sticky', compact);
      this.root.classList.toggle('moz-header--compact', compact);
      this.root.classList.toggle('shopify-section-header-hidden', shouldHide);

      if (shouldHide) this.closePanels();
      if (Math.abs(scrollDelta) >= this.scrollDeltaThreshold || scrollTop <= 18) {
        this.lastScrollTop = scrollTop;
      }
    }

    closePanels() {
      this.root.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
      this.root.querySelectorAll('.list-menu--megamenu-visible').forEach((item) => item.classList.remove('list-menu--megamenu-visible'));
    }
  }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', MoztechStickyHeader);
  }

  const mount = () => {
    const root = document.querySelector('.shopify-section-header');
    if (!root) return;

    const previous = window[controllerKey];
    if (previous && typeof previous.destroy === 'function') previous.destroy();

    const abortController = new AbortController();
    const { signal } = abortController;
    const header = root.querySelector('.header');
    const search = root.querySelector('.header__search');
    const searchDetails = search?.querySelector('details');
    const searchSummary = search?.querySelector('summary');
    const searchInput = search?.querySelector('input[type="search"]');
    const searchForm = search?.querySelector('.search__form');
    const accountDetails = root.querySelector('.header__account details');
    const accountSummary = accountDetails?.querySelector('summary');
    const drawerElement = root.querySelector('header-drawer');
    const drawer = drawerElement?.querySelector('.menu-drawer');
    const drawerMainDetails = drawerElement?.querySelector(':scope > details');
    const drawerLevels = drawerElement
      ? Array.from(drawerElement.querySelectorAll('.menu-drawer__level'))
      : [];
    let searchTimer = 0;

    const closeMegaMenus = () => {
      root.querySelectorAll('.list-menu--megamenu-visible').forEach((item) => {
        item.classList.remove('list-menu--megamenu-visible');
        const panel = item.querySelector(':scope > .mega-menu');
        if (panel) panel.style.display = 'none';
      });
    };

    const closeUtilityPanels = (except) => {
      if (searchDetails && searchDetails !== except) searchDetails.removeAttribute('open');
      if (accountDetails && accountDetails !== except) accountDetails.removeAttribute('open');
    };

    const syncHeaderHeight = () => {
      if (!header) return;
      const height = Math.ceil(header.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    };

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(syncHeaderHeight)
      : null;

    if (header && resizeObserver) resizeObserver.observe(header);
    syncHeaderHeight();

    const openSearch = () => {
      if (!searchDetails || !searchSummary || !searchInput) return;
      closeUtilityPanels(searchDetails);
      closeMegaMenus();
      if (!searchDetails.open) searchSummary.click();
      window.requestAnimationFrame(() => searchInput.focus({ preventScroll: true }));
    };

    if (searchSummary) {
      searchSummary.addEventListener('click', () => {
        if (searchDetails?.open) return;
        closeUtilityPanels(searchDetails);
        closeMegaMenus();
      }, { signal });
    }

    if (accountSummary) {
      accountSummary.addEventListener('click', () => {
        if (accountDetails?.open) return;
        closeUtilityPanels(accountDetails);
        closeMegaMenus();
      }, { signal });
    }

    document.addEventListener('keydown', (event) => {
      const target = event.target;
      const isTyping = target instanceof HTMLElement
        && (target.matches('input, textarea, select') || target.isContentEditable);
      const usesShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';

      if (usesShortcut && !isTyping) {
        event.preventDefault();
        openSearch();
      }
    }, { signal });

    if (searchForm) {
      searchForm.addEventListener('submit', () => {
        if (!searchInput?.value.trim()) return;
        window.clearTimeout(searchTimer);
        searchForm.classList.add('is-searching');
        searchTimer = window.setTimeout(() => searchForm.classList.remove('is-searching'), 720);
      }, { signal });
    }

    const syncDrawerDepth = () => {
      if (!drawer) return;
      const depth = drawerLevels.reduce((maxDepth, details) => {
        if (!details.open) return maxDepth;
        return Math.max(maxDepth, details.classList.contains('menu-drawer__level--two') ? 2 : 1);
      }, 0);
      drawer.dataset.mozDrawerDepth = String(depth);
    };

    drawerLevels.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) {
          const levelClass = details.classList.contains('menu-drawer__level--two')
            ? '.menu-drawer__level--two'
            : '.menu-drawer__level--one';
          details.parentElement?.parentElement?.querySelectorAll(`:scope > li > ${levelClass}[open]`).forEach((sibling) => {
            if (sibling !== details) sibling.removeAttribute('open');
          });
          details.querySelector(':scope > .menu-drawer__submenu .menu-drawer__inner-submenu')?.scrollTo(0, 0);
        }
        syncDrawerDepth();
      }, { signal });
    });

    if (drawerMainDetails) {
      drawerMainDetails.addEventListener('toggle', () => {
        if (drawerMainDetails.open) {
          searchDetails?.removeAttribute('open');
          root.querySelector('.header__account details[open]')?.removeAttribute('open');
          window.requestAnimationFrame(syncDrawerDepth);
          return;
        }

        drawerLevels.forEach((details) => {
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
        });
        drawer?.querySelectorAll('.menu-drawer__navigation-container, .menu-drawer__inner-submenu').forEach((panel) => {
          panel.scrollTop = 0;
        });
        syncDrawerDepth();
      }, { signal });
    }

    const megaItems = Array.from(root.querySelectorAll('.list-menu--megamenu'));
    const syncMegaAria = () => {
      megaItems.forEach((item) => {
        const trigger = item.querySelector(':scope > .header__menu-item');
        const panel = item.querySelector(':scope > .mega-menu');
        if (!trigger || !panel) return;
        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-controls', panel.id);
        trigger.setAttribute('aria-expanded', item.classList.contains('list-menu--megamenu-visible') ? 'true' : 'false');
      });
    };

    syncMegaAria();

    const megaObserver = typeof MutationObserver === 'function'
      ? new MutationObserver(syncMegaAria)
      : null;

    megaItems.forEach((item) => {
      if (megaObserver) megaObserver.observe(item, { attributes: true, attributeFilter: ['class'] });
      item.addEventListener('pointerenter', () => closeUtilityPanels(), { signal });
      item.addEventListener('focusin', () => closeUtilityPanels(), { signal });
      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const trigger = item.querySelector(':scope > .header__menu-item');
        const panel = item.querySelector(':scope > .mega-menu');
        item.classList.remove('list-menu--megamenu-visible');
        if (panel) panel.style.display = 'none';
        syncMegaAria();
        trigger?.focus();
      }, { signal });
    });

    window[controllerKey] = {
      destroy() {
        abortController.abort();
        resizeObserver?.disconnect();
        megaObserver?.disconnect();
        window.clearTimeout(searchTimer);
      }
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.classList?.contains('shopify-section-header')) mount();
  });
})();
